import { Copy01Icon, CopyCheckIcon } from '@hugeicons/core-free-icons';
import { type MouseEvent, useEffect, useState } from 'react';
import { Icon } from '@/components/primitives/icon';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { copyLabel, copyText } from '@/lib/copy';
import { cn } from '@/lib/utils';

/** How long the check mark shows after a copy (§6.10). */
const COPIED_MS = 1500;

export interface CopyButtonProps {
  value: string;
  /**
   * Names what is copied, for the toast "Copied <label>" and the button's
   * accessible name. Defaults to the value, middle-truncated.
   */
  label?: string;
  /** Disabled until something else happens (MaskedValue: "Reveal to copy"). */
  disabled?: boolean;
  /** Tooltip while disabled, for example "Reveal to copy". */
  disabledReason?: string;
  className?: string;
}

/**
 * A 24 px icon button that copies `value` (§6.10). The icon swaps to a check
 * for 1.5 s and the toast "Copied <label>" confirms it. Clicks never reach the
 * row or chip underneath.
 */
export function CopyButton({
  value,
  label,
  disabled = false,
  disabledReason,
  className,
}: Readonly<CopyButtonProps>) {
  const [copied, setCopied] = useState(false);
  const name = copyLabel(value, label);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  async function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    if (await copyText(value, label)) setCopied(true);
  }

  const tip = disabled
    ? (disabledReason ?? 'Nothing to copy yet')
    : `Copy ${name}`;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            disabled={disabled}
            focusableWhenDisabled
            aria-label={`Copy ${name}`}
            data-slot="copy-button"
            onClick={handleClick}
            className={cn(
              'text-muted-foreground hover:text-foreground aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
              className,
            )}
          />
        }
      >
        <Icon icon={copied ? CopyCheckIcon : Copy01Icon} size={14} />
      </TooltipTrigger>
      <TooltipContent>{copied ? 'Copied' : tip}</TooltipContent>
    </Tooltip>
  );
}
